/**
 * WebSocket Sync Manager
 *
 * Handles WebSocket connection to the backend for real-time CRDT sync.
 * Manages connection lifecycle, reconnection, and message handling.
 */

import { getVersion, isSyncStoppedForUpdate } from "./app-version.js";
import {
  MessageType,
  ConnectionState,
  encodeBytes,
  decodeBytes,
} from "./ws-protocol.js";

/** `session-invalidated` reason sent when a map's key was rotated (protocol.rs). */
const KEY_ROTATED_REASON = "dek_rotated";
import { createHeartbeatManager } from "./ws-heartbeat.js";
import { createReconnectionManager } from "./ws-reconnection.js";
import { createCompactionManager } from "./ws-compaction.js";
import { createOfflineQueueManager } from "./ws-offline-queue.js";
import { hasUnsentDeletions } from "./unsent-deletions.js";
import { encryptPayload, decryptPayload } from "./ws-encryption.js";
import { createMessageHandler } from "./ws-message-handlers.js";
import * as StorageHealth from "./storage-health.js";
import { isDocQuotaBlocked, hasDocQuotaPending } from '../services/quota-state.js';
import * as Y from "yjs";
import { getSyncUrl } from "../storage/index.js";
import { markConfirmed, unsyncedState } from "./unsynced-changes.js";
import { announceSessionEnded, noteConnectFailed, noteConnected } from "./session-check.js";
import { isLocalTxOrigin } from "./origins.js";
import { createLogger } from "../core/logger.js";
import { createPartsReader, toParts, PART_MAX } from "./ws-parts.js";
import { busyRetryDelay } from "./server-errors.js";
import { emit, Events } from "../core/events.js";

// Debug-level logger for the per-update hot path (every keystroke routes
// through _sendUpdate). console.log formats its arguments even with devtools
// closed; the logger no-ops below the configured level (warn in production).
const hotLog = createLogger("SyncManager");

// Re-export protocol types for API compatibility
export { MessageType, ConnectionState };

// Global counter for debugging WebSocket instances
let _wsInstanceCounter = 0;
const DELTA_ACK_TIMEOUT_MS = 30000;
// At most this many changes awaiting the server's acknowledgement: beyond,
// new edits wait in the send buffer, merged into one change sent at the next
// acknowledgement (_scheduleDeltaFlush). With acks in ~50 ms it never holds;
// on a slow or overloaded server each writer sends fewer, larger changes
// instead of a queue that grows (staging stress test, 2026-10-17).
const SEND_WINDOW = 4;
// Outgoing delta debounce window. Batches per-keystroke Yjs updates into one
// merged delta so a held-down key (≈30 Hz repeat) doesn't push us over the
// server's 30/sec rate limit. 250ms keeps remote echo feeling reactive.
const DELTA_FLUSH_MS = 250;
// Persistent-failure escalation. Transient server errors (rate-limit) are
// normally recoverable via Yjs state-vector reconciliation, so they don't
// trip the durability gate on their own. But if they fire repeatedly
// without any successful delta-ack in between, sync is actually stuck —
// escalate to markBroken so the user sees the modal and refreshes.
const MAX_CONSECUTIVE_TRANSIENT_ERRORS = 5;

/**
 * SyncManager class
 * Manages WebSocket connection and sync protocol
 */
export class SyncManager {
  constructor(options = {}) {
    this._instanceId = ++_wsInstanceCounter;
    // Sent when connecting: the server closes this channel's earlier socket,
    // which is dead if it connects again, instead of counting it.
    this._clientId = crypto.randomUUID();
    // The server refused the last connection: too many for this account (4429).
    this._refusedTooMany = false;

    this.docId = null;
    this.docManager = null;
    this.ws = null;
    this.state = ConnectionState.DISCONNECTED;

    // Callbacks
    this.onStateChange = options.onStateChange || null;
    this.onPresenceChange = options.onPresenceChange || null;
    this.onError = options.onError || null;
    this.onRemoteUpdate = options.onRemoteUpdate || null;
    this.onUnshared = options.onUnshared || null; // Called when share is revoked
    this.onAutoUnshared = options.onAutoUnshared || null; // Called when share auto-stops (0 users)
    this.onInitialSync = options.onInitialSync || null; // Called once when initial sync completes (SYNCED state)
    this.onDekRotated = options.onDekRotated || null; // Called when share DEK is rotated (shareId) => void
    // Called from ws-message-handlers when an AES-GCM auth-tag failure
    // suggests our local DEK is stale relative to the server. Implementations
    // should refetch the wrapped DEK from doc_keys, update the local vault,
    // call setEncryptionKey() with the new key, and resolve to that key
    // (or null if the server's wrap matches what we already had).
    this.onKeyOutOfSync = options.onKeyOutOfSync || null;
    this.onSessionInvalidated = options.onSessionInvalidated || null;
    // After this many connect attempts that never reach onopen, the
    // reconnection manager gives up and onAccessDenied is invoked. null
    // disables the cap (default for personal/main docs that should keep
    // retrying forever). Linked docs typically pass a small number so a
    // revoked recipient stops looping. Network blips after a previous
    // successful connect still retry indefinitely.
    this.maxAttemptsWithoutSuccess = options.maxAttemptsWithoutSuccess ?? null;
    this.onAccessDenied = options.onAccessDenied || null;
    // A map open by its link, not as a member (engine/sync/link-sharing.js):
    // () => Promise<string|null>, a one-time ticket for each socket, null
    // once the link no longer works (then nothing reconnects).
    this.linkTicket = options.linkTicket || null;
    // () => true while the user may only view this map: nothing is sent
    // (no deltas, no compaction, no reconciliation). Local data stays in
    // Yjs and IndexedDB and is reconciled if the role becomes writable.
    this.isReadOnly = options.isReadOnly || null;
    this.onPermissionDenied = options.onPermissionDenied || null;
    // Changes this copy has and the server will not store (a viewer now):
    // { offline } says whether some were made while disconnected (decision 4
    // of docs/plans/2026-10-02-crdt-schema-v2.md).
    this.onChangesRefused = options.onChangesRefused || null;
    this._serverDeniedWrites = false; // set when the server refused a write on this connection
    // Unconfirmed changes include some made while disconnected (or left by
    // an earlier session); cleared once the server has everything.
    this._offlineChanges = false;
    this._roleChangeGap = false; // between a close for a role change and the next reconnect step
    this._connectedOnce = false;
    this._stopOfflineWatch = null;
    this._stopFormatWatch = null;
    // Keys this session used before a rotation, newest first: updates a
    // member sent with the old key just before the rotation stay readable.
    this._previousKeys = [];
    // Set by a dek-rotated notice, cleared once the key is replaced or the
    // current key reads the server's baseline. Sends wait meanwhile.
    this._awaitingNewKey = false;
    // SHA-256 (hex) of every stored payload this client's state includes
    // (baseline, deltas, broadcasts, own sends). Sent with compactions so the
    // server keeps rows the snapshot does not cover.
    this._observedPayloads = new Set();
    // SHA-256 (hex) of payloads that could not be decrypted (see quarantine).
    this._unreadablePayloads = new Set();
    this._initialSyncFired = false; // Prevent firing onInitialSync multiple times per connection

    // Reconnection settings (used to configure reconnection manager)
    this.reconnectDelay = 1000; // Start with 1 second
    this.maxReconnectDelay = 30000; // Max 30 seconds between attempts
    this.offlineThresholdMs = 30000; // 30 seconds
    this._stopped = false; // Set to true when explicitly disconnected to prevent auto-reconnect
    this._reconnection = null; // ReconnectionManager instance

    // Heartbeat settings (used to configure heartbeat manager)
    this.heartbeatInterval = 30000; // 30 seconds
    this.heartbeatTimeout = 10000; // 10 seconds to receive ack
    this._heartbeat = null; // HeartbeatManager instance

    // Offline queue manager
    this._offlineQueue = null; // Created in connect()

    // Encryption key for this document (DEK)
    this.encryptionKey = null;

    // Unsubscribe function for doc updates
    this._unsubscribeDocUpdates = null;

    // Promise that resolves when disconnect is complete
    this._disconnectPromise = null;

    // Promise that resolves when connect is complete
    this._connectPromise = null;

    // Compaction settings and manager
    this.MAX_DELTAS_BEFORE_COMPACT = 500; // Force compact after 500 deltas
    this._compaction = null; // CompactionManager instance
    this._syncStallTimer = null;
    this._pendingDeltaAcks = new Map();
    this._nextDeltaId = 1;

    // Delta send debounce buffer
    this._pendingDeltas = [];
    this._deltaFlushTimer = null;
    // The send buffer waits for an acknowledgement (SEND_WINDOW).
    this._heldForAcks = false;

    // Persistent transient-error tracker. ONLY mutated by _recordTransientError
    // (increment) and _resetTransientErrorCount (zero). Touch nothing else.
    this._consecutiveTransientErrors = 0;

    // Server busy (backend save_gate.rs): refusals in a row, the pending
    // resend, and whether the indicator says so (_onServerBusy).
    this._busyInRow = 0;
    this._busyRetryTimer = null;
    this._serverBusy = false;

    // Offline queue limits
    this.MAX_OFFLINE_QUEUE_SIZE = 500; // Max items before dropping oldest
  }

  // ==================== Delta Sync: Compaction Methods ====================

  /**
   * Create and start the compaction manager
   * Called after initial sync is complete
   */
  _startCompactionTimer() {
    // Create compaction manager if not exists
    if (!this._compaction) {
      this._compaction = createCompactionManager({
        deltaThreshold: this.MAX_DELTAS_BEFORE_COMPACT,
        onCompact: (reason) => this._performCompaction(reason),
        // The snapshot stored every change made before it (including one
        // the server refused at the change cap).
        onStored: () => this._noteServerHasAll(),
        onFailure: ({ failures, lastError }) => {
          console.error(`[SyncManager] Compaction failed ${failures} times: ${lastError}`);
          StorageHealth.markBroken(`compaction_failed: ${lastError}`);
        },
        log: (msg) => console.log(`[SyncManager] ${msg}`),
      });
    }
    this._compaction.start();
  }

  /**
   * Stop compaction timer
   */
  _stopCompactionTimer() {
    this._compaction?.stop();
  }

  /**
   * Public method to force a compaction
   * Used when initializing a share to ensure recipients get a baseline
   * @param {string} reason - Why compaction was triggered
   * @returns {Promise<boolean>} True if compaction was sent
   */
  async forceCompaction(reason = "manual") {
    if (this._compaction) {
      return this._compaction.triggerCompaction(reason);
    }
    // Fallback if compaction manager not initialized
    return this._performCompaction(reason);
  }

  /**
   * Perform the actual compaction (send full state to server)
   * This is called by the compaction manager
   * @param {string} reason - Why compaction was triggered
   * @returns {Promise<boolean>} True if compaction was sent
   */
  async _performCompaction(reason) {
    if (!this.docManager) {
      return false;
    }
    // A viewer cannot rewrite the map's baseline (the server refuses it).
    if (this._isWriteBlocked()) {
      return false;
    }

    if (this.ws?.readyState !== WebSocket.OPEN) {
      console.log("[SyncManager] Cannot compact: WebSocket not open");
      return false;
    }
    // Too many stored updates seen to list (the server takes 10,000): a
    // snapshot without the list deletes nothing and is stored as one more
    // update, a full copy of the map each time. Sync again instead, which
    // lists only the rows stored now; the compaction manager asks again at
    // its next tick (not sent: no failure counted), with a fresh snapshot.
    // (Found 2026-10-16, known-issues.md; as the account channel does.)
    // If the rows stored are themselves over 9,000 (seen right after a
    // sync), syncing again lists as many: skip, rather than load the whole
    // map again at every tick.
    if (this._observedPayloads.size > 9000) {
      if ((this._observedAtSync ?? 0) > 9000) {
        if (!this._warnedTooManyStored) {
          this._warnedTooManyStored = true;
          console.warn("[SyncManager] Compaction skipped: over 9,000 updates stored, more than a snapshot can list");
        }
        return false;
      }
      // A sync already under way clears the list: not a second one.
      if (this.state !== ConnectionState.SYNCED) return false;
      console.log("[SyncManager] Compaction deferred: syncing again first (too many stored updates to list)");
      this._resync();
      return false;
    }

    try {
      console.log(
        `[SyncManager] Performing compaction (reason: ${reason}, deltas: ${this._compaction?.getDeltaCount() || 0})`,
      );

      const fullState = this.docManager.getFullStateForCompaction(reason);
      const rawSize = fullState.data?.length || 0;
      console.log(
        `[SyncManager] Compaction baseline: ${(rawSize / 1024).toFixed(1)} KB raw`
      );
      await this._sendUpdate(fullState);
      return true;
    } catch (err) {
      console.error("[SyncManager] Compaction failed:", err);
      throw err; // Re-throw so compaction manager knows it failed
    }
  }

  // ==================== End Delta Sync Methods ====================

  /**
   * Set the encryption key for this document
   * @param {CryptoKey} key - AES-GCM key for encrypting updates
   */
  setEncryptionKey(key) {
    if (key && !(key instanceof CryptoKey)) {
      console.error(
        "[SyncManager] setEncryptionKey called with non-CryptoKey:",
        typeof key,
        key,
      );
      throw new Error("Encryption key must be a CryptoKey object");
    }
    if (key && this.encryptionKey && key !== this.encryptionKey) {
      this._previousKeys = [this.encryptionKey, ...this._previousKeys.filter(k => k !== key)].slice(0, 3);
      this._awaitingNewKey = false;
      // The key change held the map's saves (refused as busy): over now, so
      // the pause starts again from the beginning.
      this._busyInRow = 0;
      clearTimeout(this._busyRetryTimer);
      this._busyRetryTimer = null;
    }
    this.encryptionKey = key;
    console.log(
      "[SyncManager] Encryption key set:",
      key ? "CryptoKey" : "null",
    );
  }

  /**
   * Clear the offline queue without processing.
   * Used during DEK rotation to discard deltas encrypted under the old key.
   */
  clearOfflineQueue() {
    if (this._offlineQueue) {
      this._offlineQueue.clear();
    }
  }

  /**
   * Connect to sync server for a document
   * @param {string} docId - Document ID
   * @param {YjsDocManager} docManager - The Yjs document manager
   */
  async connect(docId, docManager) {
    // Wait for any pending disconnect to complete
    if (this._disconnectPromise) {
      console.log(
        "[SyncManager] Waiting for pending disconnect to complete...",
      );
      await this._disconnectPromise;
    }

    // Wait for any pending connect to complete
    if (this._connectPromise) {
      console.log("[SyncManager] Connection already in progress, waiting...");
      return this._connectPromise;
    }

    // Guard against double-connection
    if (this.ws && this.ws.readyState !== WebSocket.CLOSED) {
      console.warn(
        "[SyncManager] Already connected or connecting, disconnecting first",
      );
      await this.disconnect();
    }

    // Preserve the offline queue across restarts of the SAME doc. restart()
    // (storage-broken recovery) documents that the queue survives — but
    // unconditionally recreating it here silently dropped queued updates
    // precisely when un-pushed local edits existed (audit B4).
    const isSameDoc = this.docId === docId && !!this._offlineQueue;

    this.docId = docId;
    this.docManager = docManager;
    this._stopped = false; // Reset stopped flag for new connection
    // Note: _initialSyncFired is reset in _connect() to handle both initial connect and reconnect

    // Preserve offline state from previous connection (for doc switches while offline)
    const wasOffline = this._reconnection?.isInOfflineState() || false;
    if (wasOffline) {
      console.log("[SyncManager] Preserving offline state for new document");
    }

    // Create reconnection manager for this connection
    this._reconnection = createReconnectionManager({
      initialDelay: this.reconnectDelay,
      maxDelay: this.maxReconnectDelay,
      offlineThreshold: this.offlineThresholdMs,
      onReconnect: () => this._connect(),
      onOfflineStatusChange: (offline) => {
        if (offline) {
          console.log("[SyncManager] Offline threshold reached, showing offline status");
          this._setState(ConnectionState.DISCONNECTED);
        } else {
          // Status will be updated by _connect() success
        }
      },
      // Cap the never-connected retries so a doc the user has lost access
      // to (server returns 403 on every upgrade) doesn't loop forever in
      // the console. After the cap, onGiveUp fires; the SyncManager
      // surfaces it as ConnectionState.ERROR + a callback so the host
      // module (e.g., crdt-bridge for linked docs) can mark the doc as
      // access-denied. Network blips after a successful connect still
      // retry indefinitely (everConnected gates the cap).
      maxAttemptsWithoutSuccess: this.maxAttemptsWithoutSuccess ?? null,
      onGiveUp: () => {
        console.warn("[SyncManager] Giving up: never connected after max attempts (likely access denied)");
        this._setState(ConnectionState.ERROR);
        if (typeof this.onAccessDenied === "function") {
          try { this.onAccessDenied(); } catch (err) {
            console.warn("[SyncManager] onAccessDenied callback threw:", err);
          }
        }
      },
      log: (msg) => console.log(`[SyncManager] ${msg}`),
      initialOffline: wasOffline, // Preserve offline state across doc switches
    });

    // Create offline queue manager for this connection — unless we are
    // reconnecting to the same doc, in which case queued updates must
    // survive (they're flushed once sync-step2 completes).
    if (!isSameDoc) {
      this._offlineQueue = createOfflineQueueManager({
        maxSize: this.MAX_OFFLINE_QUEUE_SIZE,
        log: (msg) => console.log(`[SyncManager] ${msg}`),
        onDropped: (queueSize) => {
          // Local compaction: replace entire queue with a single fullState snapshot.
          // This is safe because the Y.Doc contains the merged result of all queued
          // deltas, so a fullState update supersedes every individual delta.
          if (this.docManager) {
            try {
              const fullState = this.docManager.getFullStateForCompaction("offline-queue-compact");
              // Keep the oldest edit's time: the heartbeat reports its age.
              const queuedAt = Math.min(Date.now(), ...this._offlineQueue.getQueue().map(item => item.queuedAt ?? Date.now()));
              this._offlineQueue.clear();
              this._offlineQueue.enqueue({
                updateInfo: fullState,
                queuedAt,
              });
              console.log(`[SyncManager] Offline queue compacted: ${queueSize} items → 1 fullState`);
            } catch (err) {
              console.warn("[SyncManager] Offline queue compaction failed, keeping queue as-is:", err.message);
            }
          }
        },
      });
    } else if (!this._offlineQueue.isEmpty()) {
      console.log(`[SyncManager] Preserving offline queue across reconnect (${this._offlineQueue.size()} items)`);
    }

    // If already offline, stay in DISCONNECTED while trying to connect
    // Otherwise show CONNECTING
    this._setState(wasOffline ? ConnectionState.DISCONNECTED : ConnectionState.CONNECTING);

    this._connectPromise = this._doConnect();
    try {
      await this._connectPromise;
    } finally {
      this._connectPromise = null;
    }
  }

  /**
   * Internal connect implementation
   */
  async _doConnect() {
    try {
      await this._connect();
    } catch (error) {
      console.error("[SyncManager] Connection failed:", error);
      // If already offline, stay in DISCONNECTED; otherwise show ERROR briefly
      if (!this._reconnection?.isInOfflineState()) {
        this._setState(ConnectionState.ERROR);
      }
      this._reconnection?.schedule();
    }
  }

  /**
   * Internal connect method
   */
  async _connect() {
    const self = this;
    // Until the reconnect step has sent what was changed while
    // disconnected, "nothing pending" does not mean the server has it all.
    this._reconciled = false;
    if (!this._connectedOnce) {
      this._connectedOnce = true;
      // Left unconfirmed by an earlier session: made offline, or not
      // confirmed before the map was closed.
      if (this.docId && unsyncedState(this.docId) === "dirty") this._offlineChanges = true;
    }
    this._watchOfflineChanges();
    this._watchFormat();
    // Reset initialSyncFired so onInitialSync can fire for this connection/reconnection
    // This ensures post-sync callbacks work after auto-reconnect, not just initial connect
    this._initialSyncFired = false;

    // A map open by its link: each socket opens with a one-time ticket
    // (asked with the link; its token never goes in an address). None: the
    // link no longer works, there is nothing to reconnect to.
    let ticket = "";
    if (this.linkTicket) {
      const asked = await this.linkTicket();
      if (!asked) {
        console.log("[SyncManager] The map's link no longer works: not reconnecting");
        this._stopped = true;
        this._reconnection?.stop();
        this._setState(ConnectionState.DISCONNECTED);
        return;
      }
      ticket = `&link_ticket=${encodeURIComponent(asked)}`;
    }

    // Build WebSocket URL via the storage adapter so the Tauri build can
    // route to a configured server (or short-circuit when sync is disabled).
    const url = getSyncUrl(`/sync/${this.docId}?client=${this._clientId}${ticket}`);
    if (!url) {
      // Sync is unavailable in this runtime (e.g. Tauri local-only mode).
      // Stay disconnected without scheduling reconnects; callers that
      // depend on sync should gate via isSyncAvailable() before connect().
      console.log("[SyncManager] Sync unavailable, skipping connect");
      this._setState(ConnectionState.DISCONNECTED);
      return;
    }

    // Create message handler for this connection
    this._messageHandler = createMessageHandler({
      getDocId: () => this.docId,
      getDocManager: () => this.docManager,
      getEncryptionKey: () => this.encryptionKey,
      getPreviousKeys: () => this._previousKeys,
      getCompaction: () => this._compaction,
      getHeartbeat: () => this._heartbeat,
      setState: (state) => this._setState(state),
      startCompactionTimer: () => this._startCompactionTimer(),
      disconnect: () => this.disconnect(),
      callbacks: {
        get onPresenceChange() { return self.onPresenceChange; },
        get onError() { return self.onError; },
        get onRemoteUpdate() { return self.onRemoteUpdate; },
        get onUnshared() { return self.onUnshared; },
        get onAutoUnshared() { return self.onAutoUnshared; },
        get onDekRotated() { return self.onDekRotated; },
        get onKeyOutOfSync() { return self.onKeyOutOfSync; },
        onSessionInvalidated: (reason) => {
          if (reason === KEY_ROTATED_REASON) this._noteKeyRotationClose();
          // Closed because this user's role changed: changes made until the
          // reconnect are part of that moment, not offline work.
          if (reason === "permission_changed") this._roleChangeGap = true;
          self.onSessionInvalidated?.(reason);
        },
        onDeltaAck: (deltaId) => this._handleDeltaAck(deltaId),
        onHeartbeatAck: () => this._onHeartbeatAck(),
        onCompactionAck: () => this._noteServerHasAll(),
        onQuotaError: () => this._clearPendingDeltaAcks(),
        // The server reads messages in parts: larger ones go that way (_send).
        onServerReadsParts: (yes) => { this._serverReadsParts = yes; },
        onSyncStep2Applied: (serverUpdates) => {
          // How many stored rows a sync lists (see _performCompaction).
          this._observedAtSync = this._observedPayloads.size;
          this._warnedTooManyStored = false;
          return this._reconcileClientAhead(serverUpdates);
        },
        onSyncStep2Start: () => {
          this._observedPayloads.clear();
          this._unreadablePayloads.clear();
        },
        onPayloadApplied: (payload) => this._rememberPayload(this._observedPayloads, payload),
        onRemoteApplied: (update) => this._receivedSinceSync?.push(update),
        onPayloadUnreadable: (payload) => this._rememberPayload(this._unreadablePayloads, payload),
        onDekRotationNotice: () => { this._awaitingNewKey = true; },
        onResyncNeeded: () => this._resync(),
        onKeyConfirmed: () => { this._awaitingNewKey = false; },
        // Transient server error (rate-limit, too many requests). The
        // rejected delta lives in local Yjs + IndexedDB and is pushed by
        // the client-ahead reconciliation after the next sync-step2, or by
        // the next compaction; clear the pending ack trackers so their 30s
        // timeout doesn't trip markBroken. _recordTransientError keeps the
        // safety net intact for the case where transient errors fire
        // persistently without recovery.
        onTransientError: (reason) => {
          this._clearPendingDeltaAcks();
          this._recordTransientError(reason);
          this._retryAfterTransientError();
        },
        // The server was busy and stored nothing: send again after a pause.
        onServerBusy: (info) => this._onServerBusy(info),
        // The server asked this writer to compact (the map holds many
        // changes since its last compaction).
        onCompactionRequested: () => { void this.forceCompaction("server-asked"); },
        // The map is at its change cap: compact now; the snapshot stores the
        // refused change. Only if no compaction can run (the local copy may
        // be incomplete) is saving really stopped.
        onCompactionNeeded: async (reason) => {
          this._clearPendingDeltaAcks();
          const sent = await this.forceCompaction("server-cap");
          if (!sent && !this._compaction?.getPendingCompaction?.()) {
            StorageHealth.markBroken(`compaction_required_blocked: ${reason}`);
          }
        },
        // The server refused a write because this user may only view the
        // map: stop writing on this connection and let the host refresh
        // the role. Not a storage failure — no global lock.
        onPermissionDenied: (reason) => {
          this._clearPendingDeltaAcks();
          this._serverDeniedWrites = true;
          try { this.onPermissionDenied?.(reason); } catch (err) {
            console.warn("[SyncManager] onPermissionDenied callback threw:", err);
          }
          this._noteChangesRefused();
        },
      },
      log: (msg) => console.log(`[SyncManager][${this.docId}] ${msg}`),
    });

    return new Promise((resolve, reject) => {
      // Closed before it opened: a refused session looks like this too (session-check.js).
      let opened = false;
      this.ws = new WebSocket(url);
      // Messages in parts (ws-parts.js): gathered per socket; this side sends
      // parts once the server says it reads them (sync-step2).
      this._partsReader = createPartsReader();
      this._serverReadsParts = false;

      this.ws.onopen = () => {
        opened = true;
        noteConnected();
        this._reconnection?.reset(); // Reset reconnection state on successful connection
        this._serverDeniedWrites = false; // a new session carries the current role
        this._keyRotationClose = false;
        // Others' changes this socket receives until the reconnect step
        // (_reconcileClientAhead).
        this._receivedSinceSync = [];
        this._setState(ConnectionState.CONNECTED);

        // Create and start heartbeat manager
        this._heartbeat = createHeartbeatManager({
          interval: this.heartbeatInterval,
          timeout: this.heartbeatTimeout,
          send: () => this._send(this._heartbeatMessage()),
          onTimeout: () => this._handleHeartbeatTimeout(),
          log: (msg) => console.log(`[SyncManager][${this.docId}] ${msg}`),
        });
        this._heartbeat.start();

        // Reset delta count before flushing offline queue
        // This ensures queued deltas don't trigger premature compaction
        this._compaction?.resetDeltaCount();

        this._startSync();
        this._subscribeToDocUpdates();
        // Offline queue is flushed after sync-step2 completes (when state
        // becomes SYNCED), not immediately on open.  This ensures the DEK
        // is up-to-date in case a rotation happened while we were offline.
        this._pendingOfflineFlush = true;
        resolve();
      };

      this.ws.onclose = (event) => {
        // 4409: closed because the map's key was rotated (or the socket was
        // opened with an old key). 4429: too many sockets for this account.
        if (event?.code === 4409) this._noteKeyRotationClose();
        this._refusedTooMany = event?.code === 4429;
        // This device's session ended (signed out elsewhere, password
        // changed): say so (the sign-in dialog). The retries go on: once
        // signed in again here, the next one connects.
        if (event?.code === 4401) {
          announceSessionEnded(event.reason);
        } else if (!opened) {
          void noteConnectFailed();
        }
        const backOff = event?.code === 4429 ? { minDelay: 30000 } : undefined;
        this._cleanup();

        // Don't reconnect if explicitly stopped by client
        if (this._stopped) {
          this._setState(ConnectionState.DISCONNECTED);
        } else {
          // Set RECONNECTING state unless offline threshold reached
          if (!this._reconnection?.isInOfflineState()) {
            this._setState(ConnectionState.RECONNECTING);
          }
          this._reconnection?.schedule(backOff);
        }
      };

      this.ws.onerror = (error) => {
        console.error("[SyncManager] WebSocket error:", error);
        reject(error);
      };

      this.ws.onmessage = (event) => {
        this._handleMessage(event.data);
      };
    });
  }

  /**
   * Start the sync protocol
   */
  /**
   * Run the sync handshake again on the open socket (e.g. after a compaction
   * refused for coverage). Edits made meanwhile are queued and flushed once
   * synced again.
   */
  _resync() {
    if (this.ws?.readyState !== WebSocket.OPEN || this._stopped) return;
    this._pendingOfflineFlush = true;
    this._startSync();
  }

  _startSync() {
    if (!this.docManager) return;
    this._receivedSinceSync ||= [];

    this._setState(ConnectionState.SYNCING);

    // Send our state vector to get missing updates
    const stateVector = this.docManager.getStateVector();
    this._send({
      type: MessageType.SYNC_STEP_1,
      stateVector: encodeBytes(stateVector),
      version: getVersion(),
      // Resync on this socket when the server says it missed changes,
      // instead of being closed (document.rs, Lagged).
      resync: true,
      // Larger messages to this client in parts (ws-parts.js).
      parts: true,
    });
  }

  /**
   * Push local state the server is missing, after sync-step2 applied.
   *
   * The backend discards the client's sync-step1 state vector, so the
   * handshake only flows server→client. Without this, edits sitting in
   * IndexedDB ahead of the server (offline edits whose flush never
   * happened before tab close) are only pushed by the next compaction —
   * which is gated on NEW deltas this connection, i.e. possibly never.
   *
   * Detection compares per-client struct clocks (Y.parseUpdateMeta) between
   * the local doc and the merged server updates. When local is ahead, the
   * diff against the server's state vector is sent as a normal tracked
   * delta (its full delete-set rides along, so pending deletions are
   * covered too). A local change that is only a deletion adds no structs:
   * when the clocks match, the delete sets are compared as well
   * (unsent-deletions.js).
   *
   * @param {Uint8Array[]} serverUpdates - decrypted updates applied from sync-step2
   */
  async _reconcileClientAhead(serverUpdates) {
    const doc = this.docManager?.doc;
    // Others' changes received on this socket since it asked are the
    // server's too (it sends a change on only once stored): without them,
    // one stored after the server's answer looked missing and was sent back
    // under this page's editing session (a duplicate, and in the map's
    // history "records don't match": simulation, 2026-10-26). This device's
    // own work never comes that way, so it is still all sent.
    const received = this._receivedSinceSync || [];
    this._receivedSinceSync = null;
    if (received.length) serverUpdates = [...(serverUpdates || []), ...received];
    if (!doc) return;
    if (this._isWriteBlocked()) {
      // A viewer: what this copy has and the server lacks will never be
      // stored (decision 4 of the schema v2 plan).
      if (this._aheadOfServer(doc, serverUpdates)) this._noteChangesRefused();
      this._roleChangeGap = false;
      return;
    }
    this._roleChangeGap = false;
    await this._pushClientAhead(doc, serverUpdates);
    if (this._isWriteBlocked()) return;
    this._reconciled = true;
    this._noteServerHasAll();
  }

  /** The reconnect step's push (see _reconcileClientAhead). */
  async _pushClientAhead(doc, serverUpdates) {
    const diff = this._aheadOfServer(doc, serverUpdates);
    if (!diff) return;
    // Sent under this page's editing session: named in the map too. Awaited:
    // the entry is then waiting to be sent before the server can confirm the
    // push (written after, it marked the map changed again once sign-out's
    // last sync had closed). Never in the way of the push.
    try { await this.docManager?.nameEditSession?.(); }
    catch (error) { console.warn("[SyncManager] Could not name the editing session:", error?.message || error); }
    console.warn(
      `[SyncManager] Local state is ahead of server — pushing ${diff.length}-byte reconciliation delta`,
    );
    await this._sendUpdate({ type: "delta", data: diff });
  }

  /**
   * What the local copy has and the server's updates lack, as an update
   * (null when nothing): new structs, or deletions only (unsent-deletions.js).
   */
  _aheadOfServer(doc, serverUpdates) {
    // Local full state (in-memory work only; nothing sent yet)
    const localFull = Y.encodeStateAsUpdate(doc);
    const localClocks = Y.parseUpdateMeta(localFull).to;
    if (localClocks.size === 0) return null; // empty local doc — nothing to push

    let serverSV = null;
    let serverClocks = new Map();
    let merged = null;
    if (serverUpdates && serverUpdates.length > 0) {
      merged = serverUpdates.length === 1
        ? serverUpdates[0]
        : Y.mergeUpdates(serverUpdates);
      serverSV = Y.encodeStateVectorFromUpdate(merged);
      serverClocks = Y.parseUpdateMeta(merged).to;
    }

    let ahead = false;
    for (const [client, clock] of localClocks) {
      if ((serverClocks.get(client) || 0) < clock) {
        ahead = true;
        break;
      }
    }
    if (!ahead && merged) ahead = hasUnsentDeletions(doc, merged);
    if (!ahead) return null;

    const diff = serverSV ? Y.encodeStateAsUpdate(doc, serverSV) : localFull;
    if (!diff || diff.length <= 2) return null; // canonical empty update
    return diff;
  }

  /**
   * Note changes made while disconnected (the update subscription only
   * runs while connected), for as long as this manager lives.
   */
  _watchOfflineChanges() {
    const doc = this.docManager?.doc;
    if (this._stopOfflineWatch || !doc) return;
    const handler = (_update, origin) => {
      if (isLocalTxOrigin(origin) && this.ws?.readyState !== WebSocket.OPEN && !this._roleChangeGap) this._offlineChanges = true;
    };
    doc.on("update", handler);
    this._stopOfflineWatch = () => doc.off("update", handler);
  }

  /** Report the format as soon as a migration (or a rollback) changes it. */
  _watchFormat() {
    const metadata = this.docManager?.metadata;
    if (this._stopFormatWatch || !metadata) return;
    const handler = (event) => {
      if (event.keysChanged.has("schemaVersion")) this._reportFormat();
    };
    metadata.observe(handler);
    this._stopFormatWatch = () => metadata.unobserve(handler);
  }

  /** The server will not store this copy's changes: tell the host once per refusal. */
  _noteChangesRefused() {
    try { this.onChangesRefused?.({ offline: this._offlineChanges }); } catch (err) {
      console.warn("[SyncManager] onChangesRefused callback threw:", err);
    }
  }

  /**
   * Handle incoming WebSocket message
   * Delegates to ws-message-handlers.js
   * @param {string} data - JSON message
   */
  async _handleMessage(data) {
    const got = this._partsReader ? this._partsReader.take(data) : { whole: data };
    if (got.pending) return;
    if (got.invalid) {
      console.warn("[SyncManager]", got.invalid);
      return;
    }
    await this._messageHandler.handleMessage(got.whole);
  }

  /**
   * Subscribe to local document updates.
   *
   * Deltas are debounced via _scheduleDeltaFlush so a burst of Yjs updates
   * (held key, batched ops) is merged with Y.mergeUpdates into one message —
   * keeps us well under the server's 30/sec rate limit. Non-delta types
   * (fullState/compaction) flush any pending delta first to preserve order.
   */
  _subscribeToDocUpdates() {
    if (!this.docManager) return;

    this._unsubscribeDocUpdates = this.docManager.onDocUpdate(
      async (updateInfo) => {
        this._noteLocalUpdateForStallWatch(updateInfo);

        if (updateInfo.type === "delta") {
          this._pendingDeltas.push(updateInfo.data);
          this._scheduleDeltaFlush();
          return;
        }

        // Non-delta (fullState): flush any pending delta first so the server
        // applies them before the new baseline.
        await this._flushPendingDeltas();

        try {
          await this._sendUpdate(updateInfo);
        } catch (err) {
          console.error("[SyncManager] Failed to send local update:", err);
          StorageHealth.markBroken(`sync_send_failed: ${err?.message || err}`);
          throw err;
        }
      },
    );
  }

  /**
   * (Re)arm the delta flush timer. Replacing the timer on each new update
   * gives us a trailing-edge debounce — flush fires `DELTA_FLUSH_MS` after
   * the last update in a burst, not the first.
   */
  _scheduleDeltaFlush() {
    if (this._deltaFlushTimer) clearTimeout(this._deltaFlushTimer);
    this._deltaFlushTimer = setTimeout(() => {
      this._deltaFlushTimer = null;
      // SEND_WINDOW changes already await their ack, or the server said it
      // is busy and a retry waits: keep merging here; the next ack, or the
      // retry, sends the buffer (_releaseHeldDeltas).
      if ((this._pendingDeltaAcks.size >= SEND_WINDOW || this._busyRetryTimer) && this.ws?.readyState === WebSocket.OPEN) {
        this._heldForAcks = true;
        return;
      }
      this._flushPendingDeltas().catch((err) => {
        console.error("[SyncManager] Delta flush failed:", err);
      });
    }, DELTA_FLUSH_MS);
  }

  /** Room in the send window again: send what waited, merged (SEND_WINDOW). */
  _releaseHeldDeltas() {
    if (!this._heldForAcks || this._pendingDeltaAcks.size >= SEND_WINDOW || this._busyRetryTimer) return;
    this._heldForAcks = false;
    this._flushPendingDeltas().catch((err) => {
      console.error("[SyncManager] Delta flush failed:", err);
    });
  }

  /**
   * Merge and send the pending delta buffer as a single delta-update.
   * Safe to call when the buffer is empty (no-op).
   */
  async _flushPendingDeltas() {
    if (this._deltaFlushTimer) {
      clearTimeout(this._deltaFlushTimer);
      this._deltaFlushTimer = null;
    }
    this._heldForAcks = false;
    if (this._pendingDeltas.length === 0) return;

    const merged = this._pendingDeltas.length === 1
      ? this._pendingDeltas[0]
      : Y.mergeUpdates(this._pendingDeltas);
    this._pendingDeltas = [];

    try {
      await this._sendUpdate({ type: "delta", data: merged });
    } catch (err) {
      console.error("[SyncManager] Failed to send merged delta:", err);
      StorageHealth.markBroken(`sync_send_failed: ${err?.message || err}`);
      throw err;
    }

    // One merged delta = one server-side row, regardless of how many
    // Yjs updates fed into it.
    if (this.ws?.readyState === WebSocket.OPEN && this._compaction) {
      this._compaction.recordDelta();
      if (this._compaction.isCompactionNeeded()) {
        console.log(
          `[SyncManager] Delta count limit reached (${this._compaction.getDeltaCount()}), forcing compaction`,
        );
        this._compaction.triggerCompaction("count_limit");
      }
    }
  }

  _noteLocalUpdateForStallWatch(updateInfo) {
    if (updateInfo?.type !== "delta") return;
    if (this.state === ConnectionState.SYNCED) {
      this._clearSyncStallTimer();
      return;
    }
    if (this._syncStallTimer) return;

    this._syncStallTimer = setTimeout(() => {
      this._syncStallTimer = null;
      if (this.state !== ConnectionState.SYNCED) {
        console.error("[SyncManager] Local updates generated while sync stayed unsynced for >60s");
        StorageHealth.markBroken("sync_stalled");
      }
    }, 60000);
  }

  _clearSyncStallTimer() {
    if (this._syncStallTimer) {
      clearTimeout(this._syncStallTimer);
      this._syncStallTimer = null;
    }
  }

  _createDeltaId() {
    return `${this._instanceId}-${Date.now().toString(36)}-${this._nextDeltaId++}`;
  }

  _trackDeltaAck(deltaId, source, updateInfo = null) {
    const timer = setTimeout(() => this._onDeltaAckLate(deltaId), DELTA_ACK_TIMEOUT_MS);

    // The raw update is kept until acknowledged: a close caused by a key
    // rotation re-queues it (encrypted again with the new key when sent).
    this._pendingDeltaAcks.set(deltaId, { timer, source, updateInfo, sentAt: Date.now() });
  }

  /**
   * A write still unconfirmed after DELTA_ACK_TIMEOUT_MS: is the server
   * there? A heartbeat now tells. Answered while the write is still
   * unconfirmed: the server is up but did not store it (the May 2026
   * incident), storage broken (_onHeartbeatAck). Not answered: the
   * connection is dead; the heartbeat closes it, and the write is queued
   * again (_cleanup) and sent once connected.
   */
  _onDeltaAckLate(deltaId) {
    const pending = this._pendingDeltaAcks.get(deltaId);
    if (!pending) return;
    console.warn("[SyncManager] Delta ack late, asking the server:", deltaId, pending.source);
    pending.late = true;
    this._heartbeat?.probe();
  }

  _onHeartbeatAck() {
    const late = [...this._pendingDeltaAcks.entries()].filter(([, pending]) => pending.late);
    if (!late.length) return;
    for (const [deltaId] of late) this._pendingDeltaAcks.delete(deltaId);
    console.error("[SyncManager] Server answers but did not confirm:", late.map(([id]) => id));
    StorageHealth.markBroken(`delta_ack_timeout: ${late.map(([, pending]) => pending.source).join(", ")}`);
  }

  /**
   * The server closed this socket because the map's key was rotated: writes
   * it had not acknowledged were refused (old key). Queue them again instead
   * of treating them as lost; they go out under the new key.
   */
  _requeuePendingDeltaAcks() {
    for (const pending of this._pendingDeltaAcks.values()) {
      clearTimeout(pending.timer);
      if (pending.updateInfo) {
        this._offlineQueue?.enqueue({ updateInfo: pending.updateInfo, queuedAt: pending.sentAt ?? Date.now() });
      }
    }
    this._pendingDeltaAcks.clear();
  }

  _handleDeltaAck(deltaId) {
    if (!deltaId) return;
    const pending = this._pendingDeltaAcks.get(deltaId);
    if (!pending) {
      console.debug("[SyncManager] Ignoring unknown delta ack:", deltaId);
      return;
    }
    clearTimeout(pending.timer);
    this._pendingDeltaAcks.delete(deltaId);
    // A confirmed durable write proves sync isn't stuck — clear the
    // persistent-failure tally. This is the SOLE reset site.
    this._resetTransientErrorCount();
    // And that the server stores again: no longer busy. The pause steps down
    // one level per ack, not to zero: with several changes in flight one ack
    // came through while others were refused, and a reset kept every pause
    // at 1 s (staging, 600 changes/s: 580,000 refusals, more than the
    // changes offered).
    this._busyInRow = Math.max(0, this._busyInRow - 1);
    this._setServerBusy(false);
    this._releaseHeldDeltas();
    this._noteServerHasAll();
  }

  /**
   * The server was busy and stored nothing (backend save_gate.rs, docs/
   * architecture/sync.md "When the server is busy"). The refused change goes
   * back into the offline queue, where its update is kept, and the queue is
   * sent again after a growing pause (busyRetryDelay): what the server can
   * store keeps being stored, the rest waits here. A refused snapshot (no
   * deltaId) is retried by the compaction tick. Not a failure: nothing counts
   * toward "storage broken"; the indicator says the server is busy.
   * @param {{deltaId?: string, retryAfterMs?: number}} info
   */
  _onServerBusy({ deltaId, retryAfterMs } = {}) {
    const pending = deltaId ? this._pendingDeltaAcks.get(deltaId) : null;
    if (pending) {
      clearTimeout(pending.timer);
      this._pendingDeltaAcks.delete(deltaId);
      // Not saved, like a change made offline: if the server then refuses
      // it for good (made a viewer meanwhile, e.g. right after a key change
      // held the map), the person is asked (keep a copy, or not) instead of
      // it being dropped (simulation, 2026-10-26: a member made a viewer
      // just after a removal lost the edits held during the key change).
      // Cleared once the server has everything (_noteServerHasAll).
      if (pending.updateInfo) this._offlineChanges = true;
      if (pending.updateInfo?.type === "delta") {
        // Back into the send buffer: merged with the edits made until the
        // retry, sent as one change (one queue item per refused send made a
        // burst at the retry: a key change's hold refuses every change for
        // seconds, and past 30 a second the server refused them as too many).
        this._pendingDeltas.unshift(pending.updateInfo.data);
        this._heldForAcks = true;
      } else if (pending.updateInfo) {
        this._offlineQueue?.enqueue({ updateInfo: pending.updateInfo, queuedAt: pending.sentAt ?? Date.now() });
      }
    }
    this._busyInRow++;
    this._setServerBusy(true);
    if (this._busyRetryTimer) return;
    this._busyRetryTimer = setTimeout(() => {
      this._busyRetryTimer = null;
      this._flushOfflineQueue()
        .then(() => this._releaseHeldDeltas())
        .catch((err) => console.warn("[SyncManager] Sending again after busy failed:", err));
    }, busyRetryDelay(this._busyInRow, retryAfterMs));
  }

  _setServerBusy(busy) {
    if (this._serverBusy === busy) return;
    this._serverBusy = busy;
    emit(Events.SERVER_BUSY_CHANGED, { busy, docId: this.docId });
  }

  /** Whether the server refused this map's last save as busy (none confirmed since). */
  isServerBusy() {
    return this._serverBusy;
  }

  /**
   * The server has every change of this map made on this device: clear its
   * dirty bit (unsynced-changes.js). Only after this connection's reconnect
   * step, and with nothing left to send or confirm.
   */
  _noteServerHasAll() {
    if (this._reconciled && this.docId && !this.hasPendingWrites()) {
      markConfirmed(this.docId);
      this._offlineChanges = false;
    }
  }

  /**
   * Persistent transient-error tally — SET site.
   * Called only from the onTransientError callback wired into the message
   * handler (single set site). Crossing the threshold trips the durability
   * gate, since at that point sync IS actually stuck.
   */
  _recordTransientError(reason) {
    this._consecutiveTransientErrors++;
    if (this._consecutiveTransientErrors >= MAX_CONSECUTIVE_TRANSIENT_ERRORS) {
      StorageHealth.markBroken(`persistent_transient_errors: ${reason}`);
    }
  }

  /**
   * Persistent transient-error tally — RESET site.
   * Called only from _handleDeltaAck (single reset site).
   */
  _resetTransientErrorCount() {
    this._consecutiveTransientErrors = 0;
  }

  _clearPendingDeltaAcks() {
    for (const pending of this._pendingDeltaAcks.values()) {
      clearTimeout(pending.timer);
    }
    this._pendingDeltaAcks.clear();
    this._releaseHeldDeltas();
  }

  /**
   * Wait until all pending delta acks have arrived (or fail with timeout).
   *
   * Used on disconnect to give the server a final window to confirm durable
   * storage of in-flight deltas. Returns true if all acks landed in time,
   * false if any are still pending after the deadline.
   */
  async flushPendingAcks(timeoutMs = 5000) {
    if (this._pendingDeltaAcks.size === 0) return true;
    const start = Date.now();
    while (this._pendingDeltaAcks.size > 0 && Date.now() - start < timeoutMs) {
      await new Promise((r) => setTimeout(r, 50));
    }
    return this._pendingDeltaAcks.size === 0;
  }

  /**
   * Unsaved-write report carried by each heartbeat, so the server can alert
   * when a connected client keeps writes it cannot save (backend
   * metrics.rs). Counts, ages and the storage-health state only.
   */
  _writeStatus(now = Date.now()) {
    const queued = this._offlineQueue?.getQueue() ?? [];
    const pending = this._pendingDeltas.length + this._pendingDeltaAcks.size + queued.length;
    let oldest = now;
    for (const entry of this._pendingDeltaAcks.values()) oldest = Math.min(oldest, entry.sentAt ?? now);
    for (const item of queued) oldest = Math.min(oldest, item.queuedAt ?? now);
    const status = {
      pending,
      oldestPendingMs: pending ? Math.max(0, Math.round(now - oldest)) : 0,
      health: StorageHealth.getState(),
    };
    const reason = StorageHealth.getReason();
    if (reason) status.healthReason = String(reason).slice(0, 80);
    return status;
  }

  /** A heartbeat never fails because of its report: an unacked one reconnects. */
  _heartbeatMessage() {
    try {
      return { type: MessageType.HEARTBEAT, ...this._writeStatus(), ...this._formatReport() };
    } catch (err) {
      console.warn("[SyncManager] Heartbeat sent without its write report:", err);
      return { type: MessageType.HEARTBEAT };
    }
  }

  /**
   * The format of the map's content once synced (1, 2, or 3 once the old
   * copy is dropped: map-format.js REPORTED_V2_WITHOUT_OLD_COPY), for the
   * server's count of maps by format (formats.rs): the content is
   * encrypted, the number is not sensitive.
   */
  _formatReport() {
    if (!this._initialSyncFired || typeof this.docManager?.reportedFormat !== "function") return {};
    return { schema: this.docManager.reportedFormat() };
  }

  /** Report the format now (after dropping the old copy), not at the next heartbeat. */
  reportFormat() {
    this._reportFormat();
  }

  /** Report the format now (once synced, and when it changes), not at the next heartbeat. */
  _reportFormat() {
    if (this.ws?.readyState !== WebSocket.OPEN || !this._initialSyncFired) return;
    this._send(this._heartbeatMessage());
  }

  hasPendingWrites() { return !!(this._pendingDeltas.length || this._pendingDeltaAcks.size || this._offlineQueue?.size() || this._sendingUpdates || this._compaction?.getPendingCompaction()); }

  /**
   * Send a CRDT update to the server
   * @param {Object} updateInfo - { type: 'delta'|'fullState', data: Uint8Array, reason?: string }
   */
  async _sendUpdate(updateInfo) {
    this._sendingUpdates = (this._sendingUpdates || 0) + 1;
    try { return await this._sendUpdateImpl(updateInfo); }
    finally { this._sendingUpdates--; }
  }

  /**
   * The server is closing this socket because the map's key was rotated:
   * hold sends until the new key is in place, and re-queue unacknowledged
   * writes when the socket closes.
   */
  _noteKeyRotationClose() {
    this._keyRotationClose = true;
    this._awaitingNewKey = true;
  }

  /** Hex SHA-256 of a stored payload (base64 ciphertext), as the server computes it. */
  async _rememberPayload(set, payload) {
    if (!payload) return;
    try {
      const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", decodeBytes(payload)));
      set.add([...digest].map(byte => byte.toString(16).padStart(2, "0")).join(""));
    } catch (err) {
      console.warn("[SyncManager] Could not hash payload:", err);
    }
  }

  /** Hashes of stored updates this client could not read (for quarantine). */
  getUnreadablePayloads() {
    return [...this._unreadablePayloads];
  }

  /** True while this channel must not send writes (viewer role). */
  _isWriteBlocked() {
    try { return this._serverDeniedWrites || !!this.isReadOnly?.(); } catch { return this._serverDeniedWrites; }
  }

  async _sendUpdateImpl(updateInfo) {
    if (this._isWriteBlocked()) {
      hotLog.debug("Read-only map: not sending", updateInfo.type);
      return;
    }
    if (updateInfo.type !== 'fullState' && isDocQuotaBlocked(this.docId)) {
      this._offlineQueue?.enqueue({ updateInfo, queuedAt: Date.now() });
      return;
    }
    if (this._awaitingNewKey) {
      // Rotation in progress: keep the update, encrypt it with the new key later.
      this._offlineQueue?.enqueue({ updateInfo, queuedAt: Date.now() });
      return;
    }
    hotLog.debug("_sendUpdate called, type:", updateInfo.type, "dataSize:", updateInfo.data?.length, "bytes");

    // Don't send updates if sync is disabled due to required update
    if (isSyncStoppedForUpdate()) {
      console.log("[SyncManager] Sync disabled, skipping update");
      return;
    }

    if (this.ws?.readyState === WebSocket.OPEN && this.state === ConnectionState.SYNCED) {
      // Online and synced: encrypt and send immediately.
      // We check SYNCED (not just OPEN) to avoid sending with a stale encryption
      // key during the sync handshake — if a DEK rotation happened while offline,
      // the new key is only available after sync-step2 completes.
      hotLog.debug("WebSocket OPEN+SYNCED, encrypting and sending...");
      const socket = this.ws;
      const encryptionKey = this.encryptionKey;
      // Freeze coverage with the snapshot: a delta received while encryption
      // awaits is not in this snapshot and must not be claimed by it.
      const observedPayloads = updateInfo.type === "fullState" && this._observedPayloads.size <= 9000
        ? [...this._observedPayloads]
        : null;
      const payload = await this._encryptPayload(updateInfo.data);

      // Re-check after the await: the socket may have been torn down while
      // encryption was in flight (disconnect() during a doc switch). Without
      // this, we'd register a delta-ack timer against a dead socket — the
      // ack can never arrive and the 30 s timeout trips the global storage-
      // broken gate. Route to the offline queue instead.
      if (this._stopped || this.ws?.readyState !== WebSocket.OPEN || socket !== this.ws ||
          this.state !== ConnectionState.SYNCED || encryptionKey !== this.encryptionKey ||
          this._awaitingNewKey ||
          (updateInfo.type !== 'fullState' && isDocQuotaBlocked(this.docId))) {
        console.warn("[SyncManager] Socket closed during encryption — queueing update instead");
        this._offlineQueue?.enqueue({
          updateInfo: updateInfo,
          queuedAt: Date.now(),
        });
        return;
      }

      let message;
      if (updateInfo.type === "fullState") {
        // State vector is sent plaintext (base64 encoded) - it's just metadata
        // containing client UUIDs and operation counts, no actual content
        const stateVectorBase64 = updateInfo.stateVector
          ? encodeBytes(updateInfo.stateVector)
          : undefined;
        message = {
          type: MessageType.FULL_STATE_UPDATE,
          payload: payload,
          stateVector: stateVectorBase64,
          trigger_reason: updateInfo.reason || "unknown",
          notify_peers: hasDocQuotaPending(this.docId),
          version: getVersion(),
          // Without it the server keeps every stored row next to the snapshot.
          ...(observedPayloads ? { observedPayloads } : {}),
        };
      } else {
        const deltaId = this._createDeltaId();
        message = {
          type: MessageType.DELTA_UPDATE,
          payload: payload,
          version: getVersion(),
          deltaId,
        };
        this._trackDeltaAck(deltaId, "live_delta", updateInfo);
      }
      await this._rememberPayload(this._observedPayloads, payload);

      hotLog.debug("Sending to server:", message.type, "payloadSize:", payload?.length, "chars (base64)");
      this._send(message);
    } else {
      // Offline: queue raw data, encrypt on flush
      // This ensures encryption uses the current key at flush time
      const queueSize = this._offlineQueue?.size() || 0;
      hotLog.info("WebSocket NOT open (state:", this.ws?.readyState, "), queuing update. Queue size:", queueSize + 1);
      this._offlineQueue?.enqueue({
        updateInfo: updateInfo,
        queuedAt: Date.now(),
      });
    }
  }

  /**
   * Flush queued updates when reconnected
   * Encrypts with current key at flush time (not queue time)
   */
  async _flushOfflineQueue() {
    if (isDocQuotaBlocked(this.docId)) return;
    if (this._awaitingNewKey || this._isWriteBlocked()) return;
    if (!this._offlineQueue || this._offlineQueue.isEmpty()) return;

    // Use the queue manager's flush with a callback that handles re-encryption
    await this._offlineQueue.flush(async (item) => {
      // Encrypt with current key
      const payload = await this._encryptPayload(item.updateInfo.data);

      let message;
      if (item.updateInfo.type === "fullState") {
        // State vector is sent plaintext (base64 encoded)
        const stateVectorBase64 = item.updateInfo.stateVector
          ? encodeBytes(item.updateInfo.stateVector)
          : undefined;
        message = {
          type: MessageType.FULL_STATE_UPDATE,
          payload: payload,
          stateVector: stateVectorBase64,
          trigger_reason: item.updateInfo.reason || "unknown",
          notify_peers: hasDocQuotaPending(this.docId),
          version: getVersion(),
        };
      } else {
        const deltaId = this._createDeltaId();
        message = {
          type: MessageType.DELTA_UPDATE,
          payload: payload,
          version: getVersion(),
          deltaId,
        };
        this._trackDeltaAck(deltaId, "offline_flush_delta", item.updateInfo);
      }

      // M-12: Verify WebSocket is still open before sending. If the connection
      // dropped mid-flush, throw to trigger the "stop on first failure" behavior
      // in the queue manager, preserving remaining items for retry.
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        throw new Error("WebSocket closed during offline queue flush");
      }
      this._send(message);
      await this._rememberPayload(this._observedPayloads, payload);

      // Count flushed deltas toward compaction threshold
      if (item.updateInfo.type === "delta" && this._compaction) {
        this._compaction.recordDelta();
      }
    });

    // Check if flushing triggered compaction threshold
    if (this._compaction?.isCompactionNeeded()) {
      console.log(
        `[SyncManager] Offline flush reached delta limit (${this._compaction.getDeltaCount()}), triggering compaction`,
      );
      this._compaction.triggerCompaction("offline_flush");
    }
  }

  /**
   * Send a message to the server
   * @param {Object} message
   */
  _send(message) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      const text = JSON.stringify(message);
      // Larger messages (a snapshot) in parts, to a server that reads them.
      if (this._serverReadsParts && text.length > PART_MAX) {
        for (const part of toParts(text)) this.ws.send(part);
      } else {
        this.ws.send(text);
      }
    }
  }

  /**
   * Encrypt a payload for transmission
   * @param {Uint8Array} data
   * @returns {string} Base64 encoded encrypted data
   */
  async _encryptPayload(data) {
    if (!this.encryptionKey) {
      throw new Error("[SyncManager] Cannot send sync data: encryption key not set");
    }

    return encryptPayload(data, this.encryptionKey, this.docId);
  }

  /**
   * Decrypt a payload from the server
   * @param {string} base64Data
   * @returns {Uint8Array}
   */
  async _decryptPayload(base64Data) {
    if (!this.encryptionKey) {
      throw new Error("[SyncManager] Cannot decrypt sync data: encryption key not set");
    }

    return decryptPayload(base64Data, this.encryptionKey, this.docId);
  }

  /**
   * Handle heartbeat timeout - connection is dead
   * Immediately starts reconnection instead of waiting for TCP close timeout
   */
  _handleHeartbeatTimeout() {
    // Stop heartbeat manager (it will have already stopped itself, but be safe)
    this._heartbeat?.stop();

    if (this.ws) {
      // Detach handlers to prevent stale onclose from interfering with reconnection
      // TCP close can take 60+ seconds when network is down
      const oldWs = this.ws;
      this.ws = null;

      oldWs.onclose = null;
      oldWs.onerror = null;
      oldWs.onmessage = null;

      if (oldWs.readyState === WebSocket.OPEN || oldWs.readyState === WebSocket.CONNECTING) {
        console.log("[SyncManager] Forcing WebSocket close due to heartbeat timeout");
        oldWs.close(4000, "Heartbeat timeout");
      }
    }

    // Don't wait for onclose - immediately start reconnecting
    this._cleanup();
    // Set RECONNECTING state unless offline threshold reached
    if (!this._reconnection?.isInOfflineState()) {
      this._setState(ConnectionState.RECONNECTING);
    }
    this._reconnection?.schedule();
  }

  // Reconnection logic is now handled by ws-reconnection.js

  /**
   * Trigger an immediate reconnection attempt.
   * Resets the attempt counter and skips backoff delay.
   * Use this when network connectivity is restored.
   */
  triggerReconnect() {
    // Don't reconnect if explicitly stopped or no doc configured
    if (this._stopped || !this.docId) {
      console.log("[SyncManager] triggerReconnect skipped (stopped or no doc)");
      return;
    }

    // Already connected or connecting
    if (this.state === ConnectionState.CONNECTED ||
        this.state === ConnectionState.SYNCING ||
        this.state === ConnectionState.SYNCED ||
        this.state === ConnectionState.CONNECTING) {
      console.log("[SyncManager] triggerReconnect skipped (already connected/connecting)");
      return;
    }

    console.log("[SyncManager] triggerReconnect - resetting attempts and reconnecting immediately");
    this._reconnection?.triggerImmediate();
  }

  /**
   * Verify the connection is actually alive after an external resume signal
   * (tab visibility returning, pageshow from bfcache, etc.).
   *
   * Mobile browsers and PWAs commonly suspend WebSockets in the background
   * without notifying JS — readyState stays OPEN while the underlying TCP
   * connection is dead. The next interval-driven heartbeat would eventually
   * detect this, but only after `interval + timeout` (~40s by default), and
   * `triggerReconnect()` is a no-op while state thinks it's SYNCED.
   *
   * Behavior:
   *   - SYNCED / SYNCING / CONNECTED: fire an immediate heartbeat. If the
   *     socket is dead the existing timeout → reconnect path takes over.
   *   - DISCONNECTED / RECONNECTING: trigger an immediate reconnect attempt,
   *     resetting backoff so the user doesn't wait through a 30s delay.
   *   - CONNECTING: leave alone; the in-flight handshake will finish.
   */
  /** Whether the server refused the last connection: too many for this account. */
  isRefusedTooMany() {
    return this._refusedTooMany;
  }

  probeConnection() {
    if (this._stopped || !this.docId) return;

    if (this.state === ConnectionState.SYNCED ||
        this.state === ConnectionState.SYNCING ||
        this.state === ConnectionState.CONNECTED) {
      this._heartbeat?.probe();
      return;
    }

    if (this.state === ConnectionState.DISCONNECTED ||
        this.state === ConnectionState.RECONNECTING) {
      this.triggerReconnect();
    }
  }

  /**
   * Forcibly tear down and re-establish the WebSocket connection.
   *
   * Unlike triggerReconnect (which is a no-op when already CONNECTING /
   * CONNECTED / SYNCING / SYNCED), restart always disconnects and reconnects.
   * Used by storage-broken recovery to escape a "zombie SYNCED" socket where
   * heartbeats pass but deltas don't.
   *
   * The offline queue and saved encryption key survive the restart; only the
   * socket and ephemeral lifecycle state are reset.
   *
   * @returns {Promise<boolean>} true if restart was attempted, false if no doc bound
   */
  async restart() {
    if (!this.docId || !this.docManager) {
      console.log("[SyncManager] restart skipped (no doc bound)");
      return false;
    }
    const docId = this.docId;
    const docManager = this.docManager;
    console.log("[SyncManager] restart: disconnecting and reconnecting");
    await this.disconnect();
    await this.connect(docId, docManager);
    return true;
  }

  /**
   * Clean up resources on disconnect
   */
  /**
   * After a temporary refusal (rate limit, the server's storage failing for a
   * moment): sync again after a backoff (1, 2, 4 … 30 s), which sends what
   * the server lacks. Five in a row without an ack in between still mark
   * storage broken (_recordTransientError).
   */
  _retryAfterTransientError() {
    if (this._transientRetryTimer) return;
    const delay = Math.min(30000, 1000 * 2 ** Math.max(0, this._consecutiveTransientErrors - 1));
    this._transientRetryTimer = setTimeout(() => {
      this._transientRetryTimer = null;
      this._resync();
    }, delay);
  }

  _cleanup() {
    if (this._transientRetryTimer) {
      clearTimeout(this._transientRetryTimer);
      this._transientRetryTimer = null;
    }
    // Busy: the queue is sent once connected again anyway.
    clearTimeout(this._busyRetryTimer);
    this._busyRetryTimer = null;
    this._setServerBusy(false);
    this._heartbeat?.stop();
    this._stopCompactionTimer();
    this._clearSyncStallTimer();

    // Flush any debounced delta buffer before tearing down. _sendUpdate will
    // route to the offline queue if the WS is no longer OPEN, so the merged
    // delta still ends up on the wire (or local Yjs alone if both fail).
    this._flushPendingDeltas().catch((err) => {
      console.warn("[SyncManager] Flush during cleanup failed:", err);
    });

    if (this._stopped) {
      this._clearPendingDeltaAcks();
    } else {
      // The connection dropped (network, server down) or the map's key was
      // rotated: writes the server had not confirmed are queued again and
      // sent once connected (the reconnect step also sends anything the
      // server lacks). Nothing is lost; the map's dirty bit stays set until
      // the server confirms (unsynced-changes.js).
      // Cut off by a key change, they were not saved: refused for good after
      // it (made a viewer meanwhile), the person is asked whether to keep
      // them, as for offline work (simulation, 2026-10-26). A role change
      // alone keeps its rule: changes refused online are dropped.
      if (this._keyRotationClose && this._pendingDeltaAcks.size) this._offlineChanges = true;
      this._requeuePendingDeltaAcks();
    }

    if (this._unsubscribeDocUpdates) {
      this._unsubscribeDocUpdates();
      this._unsubscribeDocUpdates = null;
    }
  }

  /**
   * Update connection state
   * @param {string} state
   */
  _setState(state) {
    if (this.state === state) return;

    const oldState = this.state;
    this.state = state;
    if (state === ConnectionState.SYNCED) this._refusedTooMany = false;
    console.log("[SyncManager] State:", state);

    if (this.onStateChange) {
      this.onStateChange(state, oldState);
    }

    // Notify any pending waitForSync callers
    this._notifySyncWaiters(state);

    // Flush offline queue once sync-step2 has completed (DEK is current),
    // then what waited in the send buffer.
    if (state === ConnectionState.SYNCED && this._pendingOfflineFlush) {
      this._clearSyncStallTimer();
      this._pendingOfflineFlush = false;
      this._flushOfflineQueue()
        .then(() => this._releaseHeldDeltas())
        .catch(err => {
          console.error("[SyncManager] Failed to flush offline queue:", err);
        });
    }

    if (state === ConnectionState.SYNCED) {
      this._clearSyncStallTimer();
    }

    // Fire onInitialSync exactly once when we reach SYNCED for the first time
    if (state === ConnectionState.SYNCED && !this._initialSyncFired) {
      this._initialSyncFired = true;
      this._reportFormat();
      if (this.onInitialSync) {
        console.log("[SyncManager] Initial sync complete, firing onInitialSync callback");
        try {
          this.onInitialSync();
        } catch (err) {
          console.error("[SyncManager] onInitialSync callback error:", err);
        }
      }
    }
  }

  /**
   * Get current connection state
   * @returns {string}
   */
  getState() {
    return this.state;
  }

  /**
   * Wait for connection to reach synced state
   * @param {number} timeout - Max time to wait in ms (default 10000)
   * @returns {Promise<boolean>} Resolves true if synced, false if timeout
   */
  waitForSync(timeout = 10000) {
    return new Promise((resolve) => {
      // Already synced
      if (this.state === ConnectionState.SYNCED) {
        resolve(true);
        return;
      }

      // Use a concurrent-safe waiter array instead of callback wrapping.
      // Multiple callers can wait simultaneously without overwriting each other.
      if (!this._syncWaiters) this._syncWaiters = [];
      const entry = { resolve, resolved: false };
      this._syncWaiters.push(entry);

      entry.timer = setTimeout(() => {
        if (!entry.resolved) {
          entry.resolved = true;
          this._syncWaiters = this._syncWaiters.filter(w => w !== entry);
          console.warn("[SyncManager] waitForSync timed out");
          resolve(false);
        }
      }, timeout);
    });
  }

  /** Notify all pending waitForSync callers of a state change */
  _notifySyncWaiters(newState) {
    if (!this._syncWaiters || this._syncWaiters.length === 0) return;
    const isTerminal =
      newState === ConnectionState.SYNCED ||
      newState === ConnectionState.ERROR ||
      newState === ConnectionState.DISCONNECTED;
    if (!isTerminal) return;

    const success = newState === ConnectionState.SYNCED;
    const waiters = this._syncWaiters;
    this._syncWaiters = [];
    for (const entry of waiters) {
      if (!entry.resolved) {
        entry.resolved = true;
        clearTimeout(entry.timer);
        entry.resolve(success);
      }
    }
  }

  /**
   * Disconnect from the server
   * @returns {Promise<void>} Resolves when WebSocket is fully closed
   */
  async disconnect() {
    // Note: We intentionally do NOT compact on disconnect.
    // Compaction sends the full state (can be 300KB+) which blocks map switching.
    // The server handles compaction periodically, and deltas are already synced.
    //
    // FIRST: flush the 250 ms debounce buffer while the socket is still OPEN
    // and we haven't set _stopped. Previously this flush was fire-and-forgotten
    // inside _cleanup(); its `await _encryptPayload` suspended past the
    // ack-clearing and ws-nulling below, so the resumed flush registered an
    // orphan 30 s ack timer against a dead socket → spurious global
    // "storage broken" on whatever doc the user switched to (audit A7).
    try {
      await this._flushPendingDeltas();
    } catch (err) {
      console.warn("[SyncManager] Flush before disconnect failed:", err);
    }

    // Un-acked deltas in flight get a 5 s window to be confirmed before the
    // socket closes. Still unconfirmed: nothing is lost, they are in the
    // map's saved copy and its dirty bit stays set (unsynced-changes.js); the
    // reconnect step sends them the next time the map is opened.
    if (this.ws?.readyState === WebSocket.OPEN && this._pendingDeltaAcks.size > 0 && !StorageHealth.isSigningOut()) {
      if (!await this.flushPendingAcks(5000)) {
        console.warn("[SyncManager] Leaving the map with writes not confirmed yet: sent when it is opened again");
      }
    }

    this._stopped = true; // Prevent auto-reconnect
    this._reconnection?.stop();
    this._cleanup();
    this._stopOfflineWatch?.();
    this._stopOfflineWatch = null;
    this._stopFormatWatch?.();
    this._stopFormatWatch = null;

    if (this.ws) {
      const ws = this.ws;
      this.ws = null;

      // Clear the original onclose handler to prevent duplicate handling
      ws.onclose = null;
      ws.onerror = null;
      ws.onmessage = null;

      // If already closed, resolve immediately
      if (ws.readyState === WebSocket.CLOSED) {
        console.log("[SyncManager] WebSocket already closed");
        this._setState(ConnectionState.DISCONNECTED);
        this._disconnectPromise = null;
        return Promise.resolve();
      }

      // Create promise that resolves when WebSocket closes
      this._disconnectPromise = new Promise((resolve) => {
        let timeoutId = null;

        const cleanup = () => {
          ws.removeEventListener("close", onClose);
          if (timeoutId) clearTimeout(timeoutId);
          this._disconnectPromise = null;
        };

        const onClose = () => {
          cleanup();
          resolve();
        };

        ws.addEventListener("close", onClose);

        // Also resolve after timeout to prevent hanging
        timeoutId = setTimeout(() => {
          cleanup();
          resolve();
        }, 1000);

        ws.close(1000, "Client disconnect");
      });

      this._setState(ConnectionState.DISCONNECTED);
      return this._disconnectPromise;
    } else {
      console.log("[SyncManager] No WebSocket to close");
    }

    this._setState(ConnectionState.DISCONNECTED);
    this._disconnectPromise = null;
    return Promise.resolve();
  }

  /**
   * Check if connected and synced
   * @returns {boolean}
   */
  isSynced() {
    return this.state === ConnectionState.SYNCED;
  }

  /**
   * Check if connected (but maybe not synced yet)
   * @returns {boolean}
   */
  isConnected() {
    return (
      this.state === ConnectionState.CONNECTED ||
      this.state === ConnectionState.SYNCING ||
      this.state === ConnectionState.SYNCED
    );
  }
}
